import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getServiceDimensionValues } from "slices/dimensions";

// Stable empty array: `useSelector(s => s.x || [])` would hand back a new array on every
// call, which fails react-redux's reference check and re-renders this component -- once
// per table cell -- on every dispatched action in the app.
const EMPTY = [];

/**
 * NiceLookupData Component
 *
 * Renders a value by looking up its label from dimension data.
 * Similar to fieldChild logic but as a standalone render component.
 * Supports both single values and arrays (multi-value).
 *
 * Props:
 * - children: The value(s) to look up (single value or array)
 * - serviceType: The service type (e.g., 'kernel', 'blox', 'utility', 'stateSet')
 * - serviceImplemenator: The service implementor
 * - serviceDimension: The dimension field to query
 * - lookUpLabel: The label field to use for display
 */
const NiceLookupData = (props) => {
  const dispatch = useDispatch();
  const value = props?.children;
  const serviceType = props?.serviceType;
  const serviceImplemenator = props?.serviceImplemenator;
  const serviceDimension = props?.serviceDimension;
  const lookUpLabel = props?.lookUpLabel;
  const serviceFilter = props?.serviceFilter;

  // Get dimensions from Redux
  const dimensions = useSelector((state) => state.dimensions?.dimensions ?? EMPTY);

  // Create unique dimension key
  const dimensionKey = `lookup-${serviceType}-${serviceImplemenator}-${serviceDimension}-${lookUpLabel}`;

  // Find the dimension
  const dimension = dimensions.find((d) => d.dimensionKey === dimensionKey);

  // Only request a dimension the store has no entry for. The previous condition was
  // "no values and not loading", which stays true for a dimension that legitimately
  // resolves to zero values or that failed -- the effect then re-dispatched on every
  // render, hammering the API in a loop for as long as the table was on screen. A settled
  // entry (empty or failed) now counts as answered.
  const shouldLoadDimension =
    !!serviceType &&
    !!serviceImplemenator &&
    !!serviceDimension &&
    !dimension;

  // Load dimension data if needed. Every row of a column renders in the same commit, so
  // this render-time check passes in all of them; `ifMissing` re-checks the live store
  // at dispatch, and `pending` records the key synchronously, so N rows cost one request.
  useEffect(() => {
    if (!shouldLoadDimension) {
      return;
    }

    const search = {
      type: serviceType,
      implementor: serviceImplemenator,
      field: serviceDimension,
      lookUpLabel: lookUpLabel,
      filter: serviceFilter,
      dimensionKey,
    };

    dispatch(getServiceDimensionValues({ search, ifMissing: true }));
  }, [
    dispatch,
    shouldLoadDimension,
    serviceType,
    serviceImplemenator,
    serviceDimension,
    lookUpLabel,
    serviceFilter,
    dimensionKey,
  ]);

  // Helper function to look up a single value
  const lookupSingleValue = (singleValue) => {
    if (dimension?.values && singleValue) {
      const selectedOption = dimension.values.find((opt) => opt.value === singleValue);
      if (selectedOption) {
        // If label is a template placeholder (e.g., "<group_name>"), show the value (_id) instead
        const label = selectedOption.label;
        if (label && label.startsWith("<") && label.endsWith(">")) {
          return singleValue;
        }
        return label || singleValue;
      }
    }
    return singleValue || "";
  };

  // Handle array values (multi-value)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "";
    }

    // Look up all values and join with comma
    const labels = value.map((item) => lookupSingleValue(item));
    return labels.join(", ");
  }

  // Handle single value
  return lookupSingleValue(value);
};

export default React.memo(NiceLookupData);
